import React, { useState, useEffect, useRef } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Container,
  useTheme,
  useMediaQuery,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Autocomplete,
  Paper,
  Popper,
  Chip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ShoppingBag,
  Search,
  LocationOn,
  Close,
  AccountCircle,
  Favorite,
  Phone,
  MyLocation,
  LocalOffer,
  LocalStore,
  Dashboard,
  Store,
  AddCircle,
  Logout,
  Person,
} from '@mui/icons-material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useLocation as useLocationContext, useLocationDialog } from '../contexts/LocationContext';
import { url } from '../constants';
import { ReactComponent as Logo } from "../assets/logo/Logo.svg";


const Navbar = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userData, setUserData] = useState(null);
  const [userRole, setUserRole] = useState(null); // ✅ NEW: Track user role
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  
  const dialogShownRef = useRef(false);

  const { 
    userLocation, 
    loading: locationLoading, 
    error: locationError, 
    updateLocation, 
    getLocationByGPS 
  } = useLocationContext();

  const { locationDialogOpen, openLocationDialog, closeLocationDialog } = useLocationDialog();

  const [locationSearch, setLocationSearch] = useState('');
  const [locationOptions, setLocationOptions] = useState([]);
  const [locationSearchLoading, setLocationSearchLoading] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();

  const isDealsPage = location.pathname === '/deals';

  const [favorites, setFavorites] = React.useState([]);
  
  React.useEffect(() => {
    const savedFavorites = localStorage.getItem('userFavorites');
    if (savedFavorites) {
      setFavorites(JSON.parse(savedFavorites));
    }
  }, []);

  // ✅ Check user role and authentication
  React.useEffect(() => {
    const userToken = localStorage.getItem('access_token');
    const userDataStr = localStorage.getItem('user_data');
    const vendorToken = localStorage.getItem('vendor_token');
    const vendorDataStr = localStorage.getItem('vendor_data');

    if (vendorToken && vendorDataStr) {
      // Vendor is logged in
      setIsLoggedIn(true);
      setUserData(JSON.parse(vendorDataStr));
      setUserRole('vendor');
    } else if (userToken && userDataStr) {
      // Regular user is logged in
      setIsLoggedIn(true);
      setUserData(JSON.parse(userDataStr));
      setUserRole('user');
    } else {
      // No one is logged in
      setIsLoggedIn(false);
      setUserData(null);
      setUserRole(null);
    }
  }, [location]);

  useEffect(() => {
    if (location.pathname === '/' && !userLocation && !dialogShownRef.current) {
      const timer = setTimeout(() => {
        openLocationDialog();
        dialogShownRef.current = true;
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [location.pathname, userLocation, openLocationDialog]);

  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchSuggestions([]);
      return;
    }

    const debounceTime = isMobile ? 700 : 300;
    
    const timer = setTimeout(() => {
      fetchSearchSuggestions(searchQuery);
    }, debounceTime);

    return () => clearTimeout(timer);
  }, [searchQuery, isMobile]);

  const fetchSearchSuggestions = async (query) => {
    if (window.navSearchAbortController) {
      window.navSearchAbortController.abort();
    }
    
    window.navSearchAbortController = new AbortController();
    
    setSearchLoading(true);
    try {
      const timeoutId = setTimeout(() => {
        if (window.navSearchAbortController) {
          window.navSearchAbortController.abort();
        }
      }, isMobile ? 5000 : 10000);
      
      const response = await fetch(
        `${url}/deals/search-suggestions?query=${encodeURIComponent(query)}&limit=4`,
        { signal: window.navSearchAbortController.signal }
      );
      
      clearTimeout(timeoutId);
      
      const data = await response.json();
      
      if (data.success) {
        setSearchSuggestions(data.suggestions);
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('🚫 Search cancelled or timeout');
        return;
      }
      console.error('❌ Error fetching search suggestions:', error);
      setSearchSuggestions([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleLocationSearchChange = async (query) => {
    setLocationSearch(query);

    if (query.length < 2) {
      setLocationOptions([]);
      return;
    }

    setLocationSearchLoading(true);
    try {
      const response = await fetch(
        `${url}/users/search-cities?query=${encodeURIComponent(query)}`
      );

      if (!response.ok) throw new Error('Search failed');

      const data = await response.json();
      setLocationOptions(data);
    } catch (error) {
      console.error('❌ Location search error:', error);
      setLocationOptions([]);
    } finally {
      setLocationSearchLoading(false);
    }
  };

  const handleLocationSelect = (selectedCity) => {
    if (selectedCity) {
      updateLocation(selectedCity);
      setLocationSearch('');
      setLocationOptions([]);
      closeLocationDialog();
    }
  };

  const handleRequestLocation = () => {
    getLocationByGPS();
    setTimeout(() => {
      if (userLocation) {
        closeLocationDialog();
      }
    }, 2000);
  };

  const getInitials = () => {
    if (userRole === 'vendor' && userData?.business_name) {
      return userData.business_name
        .split(' ')
        .map((name) => name[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    } else if (userData?.full_name) {
      return userData.full_name
        .split(' ')
        .map((name) => name[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return 'U';
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  // ✅ Handle logout based on role
  const handleLogout = () => {
    if (userRole === 'vendor') {
      localStorage.removeItem('vendor_token');
      localStorage.removeItem('vendor_data');
    } else {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user_data');
      localStorage.removeItem('userFavorites');
    }
    setIsLoggedIn(false);
    setUserData(null);
    setUserRole(null);
    handleProfileMenuClose();
    navigate('/');
  };

  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };

  const handleSearch = (searchText = searchQuery) => {
    const query = searchText.trim();
    if (query) {
      console.log('🔍 Searching for:', query);
      localStorage.setItem('selectedDealSearch', query);
      navigate('/deals');
      setMobileSearchOpen(false);
      setSearchQuery('');
      setSearchSuggestions([]);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    console.log('📂 Selected suggestion:', suggestion.deal_name);
    navigate(`/deal/${suggestion.id}`);
    setMobileSearchOpen(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const renderSearchOption = (props, option) => (
    <Box
      {...props}
      sx={{
        display: 'flex',
        gap: isMobile ? 1 : 2,
        alignItems: 'center',
        p: isMobile ? 1.5 : 1.5,
        cursor: 'pointer',
        borderBottom: '1px solid #f0f0f0',
        '&:hover': { bgcolor: '#f9f9f9' },
        '&:active': { bgcolor: '#f0f0f0' },
      }}
    >
      {!isMobile && option.image_url && (
        <Avatar
          src={option.image_url}
          alt={option.deal_name}
          sx={{ width: 40, height: 40 }}
        />
      )}
      <Box sx={{ flex: 1 }}>
        <Typography 
          variant="body2" 
          fontWeight={600} 
          sx={{ fontSize: isMobile ? '0.85rem' : '0.9rem' }}
        >
          {option.deal_name}
        </Typography>
        <Typography 
          variant="caption" 
          color="text.secondary" 
          sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
        >
          {option.vendor_name} • {option.category}
        </Typography>
      </Box>
    </Box>
  );

  // ✅ Define menu items based on role
  const getMenuItems = () => {
    if (userRole === 'vendor') {
      return [
        { label: 'Dashboard', path: '/vendor/dashboard', icon: <Dashboard /> },
        { label: 'My Deals', path: '/vendor/deals/view', icon: <LocalOffer /> },
        { label: 'Add Deal', path: '/vendor/add_deals', icon: <AddCircle /> },
      ];
    } else if (userRole === 'user') {
      return [
        { label: 'Home', path: '/', icon: <ShoppingBag /> },
        { label: 'Deals', path: '/deals', icon: <LocalOffer /> },
        { label: 'Vendors', path: '/vendors', icon: < LocalOffer/> },
        { label: 'Favorites', path: '/favorite', icon: <Favorite /> },
        { label: 'Profile', path: '/profile', icon: <AccountCircle /> },
      ];
    } else {
      return [
        { label: 'Home', path: '/', icon: <ShoppingBag /> },
        { label: 'Deals', path: '/deals', icon: <LocalOffer /> },
        { label: 'Vendors', path: '/vendors', icon: < LocalOffer/> },
      ];
    }
  };
  
  // ✅ Get desktop navigation items based on role
  const getDesktopNavItems = () => {
    if (userRole === 'vendor') {
      return [
        { label: 'Dashboard', path: '/vendor/dashboard' },
        { label: 'My Deals', path: '/vendor/deals/view' },
        { label: 'Add Deal', path: '/vendor/add_deals' },
      ];
    } else if (userRole === 'user') {
      return [
        { label: 'Home', path: '/' },
        { label: 'Deals', path: '/deals' },
        { label: 'Vendors', path: '/vendors' },
        { label: 'Favorites', path: '/favorite' },
        { label: 'Profile', path: '/profile' },
      ];
    } else {
      return [
        { label: 'Home', path: '/' },
        { label: 'Deals', path: '/deals' },
        { label: 'Vendors', path: '/vendors' },
      ];
    }
  };

  return (
    <>
      <AppBar
        position="sticky"
        sx={{
          bgcolor: 'white',
          color: '#1F2937',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          borderBottom: '1px solid #e5e7eb',
        }}
      >
        <Container maxWidth="lg" disableGutters={isMobile}>
          <Toolbar
            disableGutters
            sx={{
              minHeight: { xs: 64, md: 80 },
              gap: { xs: 0.5, md: 2 },
              display: 'flex',
              alignItems: 'center',
              px: { xs: 1, md: 0 },
            }}
          >
            {isMobile && (
              <IconButton color="inherit" onClick={toggleDrawer(true)} sx={{ p: 0.5 }}>
                <MenuIcon />
              </IconButton>
            )}

            <Box
              component={Link}
              to={userRole === 'vendor' ? '/vendor/dashboard' : '/'}
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                mr: { xs: 'auto', md: 2 },
                gap: { xs: 0.5, md: 1 },
              }}
            >
                          <Logo
                style={{
                  width: "clamp(48px, 5vw, 72px)",
                  height: "clamp(48px, 5vw, 72px)",
                  flexShrink: 0,
                }}
              />


              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: '#1a7f3a',
                  fontSize: { xs: '0.85rem', md: '1.2rem' },
                  whiteSpace: 'nowrap',
                }}
              >
                SeekDeals
              </Typography>
              {/* ✅ Show Vendor Badge */}
              {userRole === 'vendor' && (
                <Chip
                  label="Vendor"
                  size="small"
                  sx={{
                    bgcolor: '#1a7f3a',
                    color: 'white',
                    fontSize: '0.65rem',
                    height: 20,
                    fontWeight: 600,
                  }}
                />
              )}
            </Box>

            {/* Desktop Search - Only for users, not vendors */}
            {!isMobile && !isDealsPage && userRole !== 'vendor' && (
              <Box sx={{ display: 'flex', gap: 1, flex: 1, alignItems: 'center' }}>
                <Autocomplete
                  freeSolo
                  fullWidth
                  options={searchSuggestions}
                  getOptionLabel={(option) => 
                    typeof option === 'string' ? option : option.deal_name
                  }
                  loading={searchLoading}
                  inputValue={searchQuery}
                  onInputChange={(event, newValue) => {
                    setSearchQuery(newValue);
                  }}
                  onChange={(event, value) => {
                    if (value && typeof value === 'object') {
                      handleSuggestionClick(value);
                    }
                  }}
                  filterOptions={(x) => x}
                  PaperComponent={({ children }) => (
                    <Paper elevation={8}>{children}</Paper>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search for a product or brand"
                      size="small"
                      onKeyPress={handleKeyPress}
                      sx={{
                        bgcolor: '#f5f5f5',
                        borderRadius: 1,
                        '& .MuiOutlinedInput-root': {
                          border: 'none',
                          fontSize: '0.9rem',
                        },
                      }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {searchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={() => handleSearch()}
                                sx={{
                                  color: '#1a7f3a',
                                  '&:hover': { bgcolor: 'rgba(26, 127, 58, 0.1)' },
                                }}
                              >
                                <Search fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={renderSearchOption}
                  noOptionsText={searchQuery.length < 2 ? "Type to search..." : "No deals found"}
                />
              </Box>
            )}

            {/* Right side items */}
            <Box sx={{ 
              display: 'flex', 
              gap: { xs: 0.5, md: 1.5 }, 
              alignItems: 'center', 
              ml: 'auto'
            }}>
              {/* Location - Only for users */}
              {userRole !== 'vendor' && userLocation && (
                <Box
                  onClick={openLocationDialog}
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: { xs: 0.5, md: 1 },
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    px: { xs: 0.8, md: 1.5 },
                    py: { xs: 0.5, md: 1 },
                    borderRadius: 1,
                    bgcolor: '#f0f0f0',
                    '&:hover': { bgcolor: '#e0e0e0' },
                  }}
                >
                  <LocationOn sx={{ color: '#1a7f3a', fontSize: { xs: 16, md: 22 }, flexShrink: 0 }} />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      sx={{
                        fontSize: { xs: '0.65rem', md: '0.8rem' },
                        color: '#1a7f3a',
                        lineHeight: 1,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {userLocation.city}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: { xs: '0.55rem', md: '0.7rem' },
                        color: '#6B7280',
                        lineHeight: 1,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {userLocation.state}
                    </Typography>
                  </Box>
                </Box>
              )}

              {userRole !== 'vendor' && !userLocation && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<LocationOn sx={{ fontSize: { xs: 14, md: 18 } }} />}
                  onClick={openLocationDialog}
                  sx={{
                    color: '#1a7f3a',
                    borderColor: '#1a7f3a',
                    textTransform: 'none',
                    fontSize: { xs: '0.65rem', md: '0.8rem' },
                    px: { xs: 1, md: 1.5 },
                    py: { xs: 0.4, md: 0.6 },
                    minWidth: 'auto',
                    '&:hover': {
                      borderColor: '#1a7f3a',
                      bgcolor: 'rgba(26, 127, 58, 0.1)',
                    },
                  }}
                >
                  {isMobile ? 'Location' : 'Set Location'}
                </Button>
              )}

              {/* Mobile Search - Only for users */}
              {isMobile && !isDealsPage && userRole !== 'vendor' && (
                <IconButton
                  onClick={() => setMobileSearchOpen(!mobileSearchOpen)}
                  sx={{
                    color: '#1a7f3a',
                    p: 0.5,
                    '&:hover': { bgcolor: 'rgba(26, 127, 58, 0.1)' },
                  }}
                >
                  <Search fontSize="small" />
                </IconButton>
              )}

              {/* Favorites - Only for users on desktop */}
              {!isMobile && userRole === 'user' && (
                <Box
                  component="button"
                  onClick={() => navigate('/favorite')}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.3,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.7 },
                  }}
                >
                  <Favorite sx={{ color: '#1a7f3a', fontSize: 22 }} />
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#6B7280' }}>
                    Favorites
                  </Typography>
                </Box>
              )}

              {/* Phone - Desktop only */}
              {!isMobile && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 1,
                    bgcolor: '#1a7f3a',
                    borderRadius: 1,
                    minWidth: 'max-content',
                  }}
                >
                  <Phone sx={{ color: 'white', fontSize: 18 }} />
                  <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '0.9rem' }}>
                    +91 97553 75600
                  </Typography>
                </Box>
              )}

              {/* Profile Avatar or Login */}
              {isLoggedIn ? (
                <IconButton onClick={handleProfileMenuOpen} sx={{ p: 0.5 }}>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: '#1a7f3a', fontSize: '0.75rem' }}>
                    {getInitials()}
                  </Avatar>
                </IconButton>
              ) : (
                !isMobile && (
                  <>
                  <Button
                  variant="contained"
                  onClick={() => navigate('/vendor/login')}
                  sx={{
                    bgcolor: '#1a7f3a',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': { bgcolor: '#0f5328' },
                  }}
                >
                  Vendor Login
                </Button>
                  <Button
                    variant="contained"
                    onClick={() => navigate('/login')}
                    sx={{
                      bgcolor: '#1a7f3a',
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': { bgcolor: '#0f5328' },
                    }}
                  >
                    Login
                  </Button>
              </>
                )
              )}
            </Box>
          </Toolbar>

          {/* Mobile Search Bar */}
          {isMobile && mobileSearchOpen && !isDealsPage && userRole !== 'vendor' && (
            <Box sx={{ pb: 2, px: 1 }}>
              <Autocomplete
                freeSolo
                fullWidth
                options={searchSuggestions}
                getOptionLabel={(option) => 
                  typeof option === 'string' ? option : option.deal_name
                }
                loading={searchLoading}
                inputValue={searchQuery}
                onInputChange={(event, newValue) => {
                  setSearchQuery(newValue);
                }}
                onChange={(event, value) => {
                  if (value && typeof value === 'object') {
                    handleSuggestionClick(value);
                  }
                }}
                filterOptions={(x) => x}
                PopperComponent={(props) => (
                  <Popper 
                    {...props} 
                    style={{ width: '100%' }} 
                    placement="bottom-start"
                  />
                )}
                PaperComponent={({ children }) => (
                  <Paper 
                    elevation={8}
                    sx={{ 
                      maxHeight: '300px',
                      overflow: 'auto'
                    }}
                  >
                    {children}
                  </Paper>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    autoFocus
                    placeholder="Search for a product or brand"
                    onKeyPress={handleKeyPress}
                    size="small"
                    sx={{
                      bgcolor: '#f5f5f5',
                      borderRadius: 1,
                      '& .MuiOutlinedInput-root': {
                        border: 'none',
                        fontSize: '0.9rem',
                        ...(searchQuery && {
                          borderBottom: '2px solid #1a7f3a',
                          borderRadius: '4px 4px 0 0',
                        }),
                      },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {searchLoading && (
                            <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                              <CircularProgress color="inherit" size={18} />
                            </Box>
                          )}
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => handleSearch()}
                              sx={{
                                color: '#1a7f3a',
                                '&:hover': { bgcolor: 'rgba(26, 127, 58, 0.1)' },
                              }}
                            >
                              <Search fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={renderSearchOption}
                noOptionsText={searchQuery.length < 2 ? "Type to search..." : "No deals found"}
              />
            </Box>
          )}

          {/* Desktop Navigation - Role-based */}
          {!isMobile && (
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                pb: 0,
                borderTop: '1px solid #e5e7eb',
                pt: 1.5,
                px: 0,
              }}
            >
              {getDesktopNavItems().map((item) => (
                <Button
                  key={item.path}
                  component={Link}
                  to={item.path}
                  sx={{
                    color: '#1F2937',
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    fontWeight: 500,
                    pb: 1.5,
                    borderBottom: location.pathname === item.path ? '3px solid #1a7f3a' : 'none',
                    '&:hover': { bgcolor: 'transparent', color: '#1a7f3a' },
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          )}
        </Container>
      </AppBar>

      {/* Mobile Drawer - Role-based */}
      <Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer(false)}>
        <Box sx={{ width: 280, p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{ color: '#1a7f3a' }}>
                SeekDeals
              </Typography>
              {userRole === 'vendor' && (
                <Chip
                  label="Vendor Panel"
                  size="small"
                  sx={{
                    bgcolor: '#1a7f3a',
                    color: 'white',
                    fontSize: '0.65rem',
                    height: 18,
                    mt: 0.5,
                  }}
                />
              )}
            </Box>
            <IconButton onClick={toggleDrawer(false)}>
              <Close />
            </IconButton>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <List>
            {getMenuItems().map((item) => (
              <ListItem key={item.path} disablePadding>
                <ListItemButton
                  component={Link}
                  to={item.path}
                  onClick={toggleDrawer(false)}
                  selected={location.pathname === item.path}
                  sx={{
                    '&.Mui-selected': {
                      bgcolor: 'rgba(26, 127, 58, 0.1)',
                      borderLeft: '4px solid #1a7f3a',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {React.cloneElement(item.icon, { sx: { color: '#1a7f3a', fontSize: 22 } })}
                    <ListItemText primary={item.label} />
                  </Box>
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          <Divider sx={{ my: 2 }} />

          {!isLoggedIn && (
            <>
              <Button
              variant="contained"
              onClick={() => navigate('/vendor/login')}
              sx={{
                bgcolor: '#1a7f3a',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { bgcolor: '#0f5328' },
              }}
              >
              Vendor Login
            </Button>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  navigate('/login');
                  setDrawerOpen(false);
                }}
              >
                Login
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={() => {
                  navigate('/signup');
                  setDrawerOpen(false);
                }}
                sx={{ bgcolor: '#1a7f3a' }}
                >
                Sign Up
              </Button>
            </Box>
          </>
          )}

          {isLoggedIn && (
            <Button
            fullWidth
              variant="outlined"
              color="error"
              startIcon={<Logout />}
              onClick={() => {
                handleLogout();
                setDrawerOpen(false);
              }}
            >
              Logout
            </Button>
          )}
        </Box>
      </Drawer>

      {/* Profile Menu - Role-based */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        PaperProps={{
          sx: { mt: 1.5, minWidth: 220 },
        }}
      >
        <MenuItem disabled>
          <Box>
            <Typography variant="subtitle2" fontWeight={600}>
              {userRole === 'vendor' ? userData?.business_name : userData?.full_name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {userData?.email}
            </Typography>
            {userRole === 'vendor' && (
              <Chip
                label="Vendor"
                size="small"
                sx={{
                  bgcolor: '#1a7f3a',
                  color: 'white',
                  fontSize: '0.6rem',
                  height: 16,
                  mt: 0.5,
                }}
              />
            )}
          </Box>
        </MenuItem>
        <Divider />
        
        {userRole === 'vendor' ? (
          <>
            <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/vendor/dashboard'); }}>
              <Dashboard sx={{ mr: 1.5 }} />
              Dashboard
            </MenuItem>
            <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/vendor/deals/view'); }}>
              <LocalOffer sx={{ mr: 1.5 }} />
              My Deals
            </MenuItem>
          </>
        ) : (
          <>
            <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/profile'); }}>
              <AccountCircle sx={{ mr: 1.5 }} />
              Profile
            </MenuItem>
            <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/favorite'); }}>
              <Favorite sx={{ mr: 1.5 }} />
              Favorite Deals
            </MenuItem>
          </>
        )}
        
        <Divider />
        <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
          <Logout sx={{ mr: 1.5 }} />
          Logout
        </MenuItem>
      </Menu>

      {/* Location Dialog - Only for users */}
      {userRole !== 'vendor' && (
        <Dialog open={locationDialogOpen} onClose={closeLocationDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <LocationOn sx={{ color: '#1a7f3a', fontSize: 28 }} />
              <Typography variant="h6" fontWeight={700}>
                Select Location
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Search and select your city from the available options
              </Typography>

              <Autocomplete
                fullWidth
                options={locationOptions}
                getOptionLabel={(option) => `${option.city}, ${option.state}`}
                loading={locationSearchLoading}
                value={null}
                inputValue={locationSearch}
                onInputChange={(event, newInputValue) => {
                  handleLocationSearchChange(newInputValue);
                }}
                onChange={(event, value) => {
                  handleLocationSelect(value);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search City"
                    placeholder="Type city name..."
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {locationSearchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box
                    {...props}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 1.5,
                      borderBottom: '1px solid #e5e7eb',
                    }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {option.city}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.state}
                      </Typography>
                    </Box>
                    <LocationOn fontSize="small" sx={{ color: '#1a7f3a' }} />
                  </Box>
                )}
                noOptionsText="No cities found"
              />

              {locationError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {locationError}
                </Alert>
              )}

              <Box sx={{ my: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Divider sx={{ flex: 1 }} />
                <Typography variant="caption" color="text.secondary">
                  OR
                </Typography>
                <Divider sx={{ flex: 1 }} />
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Use your current location
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0, flexDirection: 'column', gap: 1 }}>
            <Button
              fullWidth
              variant="contained"
              size="large"
              startIcon={locationLoading ? <CircularProgress size={20} color="inherit" /> : <MyLocation />}
              onClick={handleRequestLocation}
              disabled={locationLoading}
              sx={{ bgcolor: '#1a7f3a', '&:hover': { bgcolor: '#0f5328' } }}
            >
              {locationLoading ? 'Getting Location...' : 'Use My Location'}
            </Button>
            <Button fullWidth onClick={closeLocationDialog} disabled={locationLoading}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default Navbar;
  